import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth-store';
import { apiClient } from '@/lib/api';
import { logger } from '@/lib/logger';
import {
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  X,
  SkipForward,
  Ticket,
  Activity,
  User,
  FileText,
  Receipt,
  Megaphone,
  Clock,
  CalendarDays,
  BarChart3,
  ScrollText,
} from 'lucide-react';

interface TourStep {
  title: string;
  description: string;
  icon: React.ElementType;
  highlight?: string; // CSS selector to highlight
}

const teamSteps: TourStep[] = [
  {
    title: 'Welcome to DynDev PM',
    description: 'Your all-in-one project management portal. Track projects, manage tasks, communicate with clients, handle billing, monitor site health — all from one place.',
    icon: Sparkles,
  },
  {
    title: 'Sidebar Navigation',
    description: 'Use the sidebar on the left to navigate between sections. It adapts to your role — Dashboard, Projects, Tasks, Leads, Quotes, Clients, Tickets, Invoices, Calendar, Activity, Reports, and more are all just a click away.',
    icon: LayoutDashboard,
  },
  {
    title: 'Dashboard Overview',
    description: 'Your dashboard shows key metrics at a glance — active projects, pending tasks, hours logged this week, and revenue. Recent projects and upcoming tasks are listed below for quick access.',
    icon: Activity,
  },
  {
    title: 'Projects & Stages',
    description: 'Projects move through stages: Inquiry → Quote → Design → Develop → Review → Launch. Each stage has its own tasks, milestones, and file uploads. Stage transitions trigger notifications to keep everyone informed.',
    icon: FolderKanban,
  },
  {
    title: 'Kanban Task Boards',
    description: 'Drag and drop tasks across columns (To Do → In Progress → Review → Done) to track progress visually. Switch to a list view when you prefer a structured layout. Tasks can be assigned, prioritised, and linked to projects.',
    icon: CheckSquare,
  },
  {
    title: 'Leads & Quotes',
    description: 'Manage your sales pipeline from lead capture through to quoted proposals. Create quotes with line items, notes, and validity periods. Send them to clients for review and track responses — accepted, declined, or changes requested.',
    icon: Megaphone,
  },
  {
    title: 'Clients & Invoices',
    description: 'When a quote is accepted, a client record and project are created automatically. Generate invoices for completed work and clients can pay securely via Stripe — either one-off payments or recurring subscriptions for retainers.',
    icon: Receipt,
  },
  {
    title: 'Support Tickets',
    description: 'Clients can submit support tickets for help or change requests. Track each ticket from submission through to resolution, respond with comments and file attachments, and keep a full audit trail of every interaction.',
    icon: Ticket,
  },
  {
    title: 'Time Tracking',
    description: 'Track billable hours with the built-in timer. Start and stop timers on individual tasks, view logged time per project, and generate timesheet reports. Timer keeps running even when navigating between pages.',
    icon: Clock,
  },
  {
    title: 'Activity & Calendar',
    description: 'Stay in the loop with a live activity feed of all project changes, comments, file uploads, and stage updates. The calendar view shows project milestones, task deadlines, and events at a glance.',
    icon: CalendarDays,
  },
  {
    title: 'Reports & Monitoring',
    description: 'Access detailed reports on project progress, team performance, and invite analytics. For client websites, the Site Monitoring dashboard tracks uptime, PageSpeed scores, and domain expiry dates in one place.',
    icon: BarChart3,
  },
  {
    title: 'You\'re All Set!',
    description: 'Explore at your own pace. Start by creating a project, adding a lead, or configuring your settings. Manage your profile, set up two-factor authentication, and customise your notification preferences. Need help? Look for the ? tooltips throughout the app.',
    icon: Sparkles,
  },
];

const clientSteps: TourStep[] = [
  {
    title: 'Welcome to Your Client Portal',
    description: 'This is your dedicated portal to track your projects, communicate with the DynDev team, and stay updated on progress — all in one place. Everything you see here is tailored specifically for you.',
    icon: Sparkles,
  },
  {
    title: 'Your Dashboard',
    description: 'Your dashboard gives you a snapshot of everything happening with your projects — upcoming deadlines, recent activity, project status, and team updates. It\'s your home base for a quick overview.',
    icon: LayoutDashboard,
  },
  {
    title: 'Your Projects',
    description: 'View all your projects and their current stage of development: Inquiry → Quote → Design → Develop → Review → Launch. Each project shows its status, milestones, and key dates. For live sites, the Site Health tab shows uptime, performance scores, and domain expiry.',
    icon: FolderKanban,
  },
  {
    title: 'Your Quotes',
    description: 'Review quotes sent to you by the DynDev team. View line items and totals, then accept, request changes, or decline — all from within the portal. Your response is sent to the team straight away.',
    icon: ScrollText,
  },
  {
    title: 'Project Tasks',
    description: 'See the tasks associated with your projects, who\'s working on them, and their progress. Tasks move through stages: To Do → In Progress → Review → Done. You can track everything in real time.',
    icon: CheckSquare,
  },
  {
    title: 'Project Files & Previews',
    description: 'Access documents, images, and project files uploaded by the team. You can preview images and PDFs directly in your browser or download them for offline viewing — no extra software needed.',
    icon: FileText,
  },
  {
    title: 'Support & Tickets',
    description: 'Need help or have a change request? Submit a ticket and our team will respond. Track the status of your requests — from submission through to resolution — all in one place.',
    icon: Ticket,
  },
  {
    title: 'Invoices & Payments',
    description: 'When an invoice is ready, you\'ll receive a notification to pay securely via Stripe — either a one-off payment or a recurring subscription. Track which invoices are paid and which are outstanding.',
    icon: Receipt,
  },
  {
    title: 'Project Activity',
    description: 'Stay in the loop with a live feed of all activity on your projects — comments, file uploads, stage changes, and task updates. You\'ll never miss what\'s happening.',
    icon: Activity,
  },
  {
    title: 'Your Profile',
    description: 'Manage your account details, update your name and contact information, and configure security settings like two-factor authentication. Your data, your control.',
    icon: User,
  },
  {
    title: 'You\'re All Set!',
    description: 'You\'re ready to go. Explore your projects, review your quotes, check your tasks, or submit a ticket. If you ever need help, just open a support ticket and the team will be right with you.',
    icon: Sparkles,
  },
];

const SESSION_DISMISSED_KEY = 'onboarding-tour-dismissed';

export function OnboardingTour() {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const location = useLocation();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { user, fetchUser, onboardingTourTrigger } = useAuthStore();

  const isClient = user?.role === 'client';
  const steps = isClient ? clientSteps : teamSteps;

  useEffect(() => {
    // Don't fire the tour on invitation pages — the user hasn't reached the dashboard yet
    if (location.pathname === '/accept-quote' || location.pathname === '/set-password') {
      return;
    }

    // Show the tour if the user has not completed it yet (server-side flag)
    // AND the tour was not already dismissed this session (skipped without "don't show again")
    if (user && !user.hasCompletedOnboarding && !sessionStorage.getItem(SESSION_DISMISSED_KEY)) {
      const timer = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user, location.pathname]);

  // Watch for manual trigger (e.g. from settings page) to reopen the tour
  useEffect(() => {
    if (onboardingTourTrigger > 0 && user) {
      sessionStorage.removeItem(SESSION_DISMISSED_KEY);
      setCurrentStep(0);
      setDontShowAgain(false);
      setOpen(true);
    }
  }, [onboardingTourTrigger, user]);

  const markCompleted = async () => {
    try {
      await apiClient.post('/auth/onboarding-complete');
      await fetchUser();
    } catch (err) {
      logger.warn('[Onboarding] Failed to mark tour complete:', err instanceof Error ? err.message : err);
    }
    setOpen(false);
  };

  const handleComplete = () => {
    sessionStorage.removeItem(SESSION_DISMISSED_KEY);
    markCompleted();
  };

  const handleSkip = () => {
    if (dontShowAgain) {
      sessionStorage.removeItem(SESSION_DISMISSED_KEY);
      markCompleted();
    } else {
      // Dismissed without completing — don't show again this session
      sessionStorage.setItem(SESSION_DISMISSED_KEY, 'true');
      setOpen(false);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleStepClick = (index: number) => {
    setCurrentStep(index);
  };

  if (!open) return null;

  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/60 animate-in fade-in-0 duration-300"
        style={{ willChange: 'opacity' }}
        onClick={handleSkip}
      />

      {/* Tour card */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="w-full max-w-md pointer-events-auto animate-in zoom-in-95 fade-in-0 duration-300"
          style={{ animationDelay: '0.15s' }}
        >
          <div className="relative rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-2xl shadow-dyndev-green/5 overflow-hidden">
            {/* Progress bar — GPU-composited via scaleX */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--bg-secondary)]">
              <div
                className="h-full bg-dyndev-green origin-left duration-500 ease-out"
                style={{ transform: `scaleX(${progress / 100})`, transition: 'transform 0.5s ease-out' }}
              />
            </div>

            {/* Close button */}
            <button
              onClick={handleSkip}
              className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              title="Skip tour"
            >
              <X size={16} />
            </button>

            {/* Content */}
            <div className="p-6 pt-8">
              {/* Step indicator */}
              <div className="flex items-center gap-1.5 mb-4">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handleStepClick(i)}
                    className={`h-1.5 rounded-full ${
                      i === currentStep
                        ? 'w-6 bg-dyndev-green'
                        : i < currentStep
                        ? 'w-1.5 bg-dyndev-green/40'
                        : 'w-1.5 bg-[var(--border-color)]'
                    }`}
                    style={{ transition: 'width 0.3s ease, background-color 0.3s ease' }}
                  />
                ))}
              </div>

              {/* Icon */}
              <div className="w-12 h-12 rounded-xl bg-dyndev-green/10 flex items-center justify-center mb-4">
                <step.icon size={24} className="text-dyndev-green" />
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold mb-2">{step.title}</h3>

              {/* Description */}
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-6">
                {step.description}
              </p>

              {/* Don't show again checkbox */}
              <div className="mb-4">
                <label
                  className="flex items-center gap-2 cursor-pointer group"
                  onClick={() => setDontShowAgain(!dontShowAgain)}
                >
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                      dontShowAgain
                        ? 'bg-dyndev-green border-dyndev-green'
                        : 'border-[var(--border-color)] group-hover:border-dyndev-green/50'
                    }`}
                  >
                    {dontShowAgain && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span className="text-xs text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors select-none">
                    Don&apos;t show this again
                  </span>
                </label>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={handleSkip}
                  className="flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <SkipForward size={12} />
                  Skip
                </button>

                <div className="flex items-center gap-2">
                  {!isFirst && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrev}
                      className="gap-1"
                    >
                      <ArrowLeft size={14} />
                      Back
                    </Button>
                  )}

                  <Button
                    size="sm"
                    onClick={handleNext}
                    className="gap-1"
                  >
                    {isLast ? (
                      <>Got it!</>
                    ) : (
                      <>
                        Next
                        <ArrowRight size={14} />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Step count */}
            <div className="px-6 pb-3">
              <p className="text-[10px] text-[var(--text-secondary)] text-center">
                Step {currentStep + 1} of {steps.length}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
